import React, { useEffect, useState, useCallback } from 'react';
import AdminHeader from '../components/AdminHeader';
import ArchiveManagement from '../components/ArchiveManagement';
import { motion, AnimatePresence } from 'framer-motion';
import './SuperAdminPage.css';

function SuperAdminPage() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', password:'' });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetOpen, setResetOpen] = useState({}); // track which admin has reset form open
  const [resetValues, setResetValues] = useState({}); // map adminId -> new password value
  const [resetBusy, setResetBusy] = useState({}); // map adminId -> loading bool
  const [selectedAdmin, setSelectedAdmin] = useState(null); // admin object
  const [showArchives, setShowArchives] = useState(false); // toggle archive view
  // Per-admin activity pagination state
  const [adminActivity, setAdminActivity] = useState([]);
  const [adminActivityLoading, setAdminActivityLoading] = useState(false);
  const [adminActivityMeta, setAdminActivityMeta] = useState({ hasMore: false, offset: 0, limit: 40 });
  const [expandedLogs, setExpandedLogs] = useState({}); // track which logs have expanded meta
  const token = localStorage.getItem('token');

  const loadAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/admins`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAdmins(data);
      }
    } catch {}
  }, [token]);

  const loadLogs = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/logs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch {}
  }, [token]);

  const loadAdminActivity = useCallback(async (admin) => {
    if (!admin) return;
    setAdminActivityLoading(true);
    setAdminActivity([]);
    setAdminActivityMeta({ hasMore: false, offset: 0, limit: 40 });
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/activity/${admin._id}?limit=40&offset=0`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAdminActivity(data.entries || []);
        setAdminActivityMeta({ hasMore: data.hasMore, offset: data.offset, limit: data.limit });
      }
    } catch (e) {
      // ignore
    } finally {
      setAdminActivityLoading(false);
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      await Promise.all([loadAdmins(), loadLogs()]);
      setLoading(false);
    })();
  }, [loadAdmins, loadLogs]);

  const createAdmin = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      alert('Fill all fields');
      return;
    }
    const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/admins`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify(form) });
    if (res.ok) {
      const created = await res.json();
      setAdmins(prev => [created, ...prev]);
      setForm({ firstName:'', lastName:'', email:'', password:'' });
    } else {
      const msg = await res.json().catch(()=>({ message:'Failed' }));
      alert(msg.message || 'Failed to create admin');
    }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm('Delete this admin?')) return;
    const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/admins/${id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${token}` } });
    if (res.ok) {
      setAdmins(prev => prev.filter(a => a._id !== id));
    } else {
      alert('Failed to delete');
    }
  };

  const resetPassword = async (id) => {
    const newPass = resetValues[id];
    if (!newPass || newPass.length < 8) { alert('Password must be at least 8 characters'); return; }
    setResetBusy(b => ({ ...b, [id]: true }));
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/admins/${id}/password`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }, body: JSON.stringify({ newPassword: newPass }) });
      if (res.ok) {
        alert('Password updated');
        setResetValues(v => ({ ...v, [id]: '' }));
        setResetOpen(o => ({ ...o, [id]: false }));
      } else {
        const msg = await res.json().catch(()=>({ message:'Failed'}));
        alert(msg.message || 'Failed to update');
      }
    } finally {
      setResetBusy(b => ({ ...b, [id]: false }));
    }
  };

  const exportActivityLogs = () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }
    // CSV headers
    const headers = ['Timestamp', 'Category', 'Action', 'Actor Email', 'Actor Role', 'Target', 'Meta'];
    
    // Convert logs to CSV rows
    const rows = logs.map(l => {
      const timestamp = new Date(l.timestamp).toLocaleString('en-US');
      const meta = l.meta ? JSON.stringify(l.meta).replace(/"/g, '""') : '';
      return [
        `"${timestamp}"`,
        `"${l.category || ''}"`,
        `"${l.action || ''}"`,
        `"${l.actorEmail || ''}"`,
        `"${l.actorRole || ''}"`,
        `"${l.target || ''}"`,
        `"${meta}"`
      ].join(',');
    });
    
    // Combine headers and rows
    const csv = [headers.join(','), ...rows].join('\n');
    
    // Create and download file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `activity-logs-${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
    <div className="super-admin-page">
      <div style={{ width:'100%', background:'#175c36', minHeight:32, position:'sticky', top:0, zIndex:1001 }} />
      <div style={{ width:'100%', background:'rgba(255,255,255,0.95)', backdropFilter:'blur(10px)', position:'sticky', top:32, zIndex:1000 }}>
        <div style={{ width:'100%', maxWidth:'1800px', margin:'0 auto' }}>
          <AdminHeader />
        </div>
      </div>

      <div className="super-admin-container">
        <motion.div 
          className="super-admin-header"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
        >
          <div>
            <h1 className="super-admin-title">Super Admin Panel</h1>
            <p className="super-admin-subtitle">Manage administrators and monitor system activity</p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowArchives(!showArchives)}
            style={{ marginLeft: 'auto' }}
          >
            {showArchives ? 'Show Admin Panel' : 'Manage Archives'}
          </button>
        </motion.div>

        {showArchives ? (
          <ArchiveManagement />
        ) : loading ? (
          <div className="loading-container">
            <div className="loading-spinner" role="status" aria-label="Loading"></div>
          </div>
        ) : (
          <div className="super-admin-grid">
            {/* Admins Management Card */}
            <motion.div 
              className="super-admin-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.05, ease: "easeOut" }}
            >
              <div className="super-admin-card-header">
                <h2 className="super-admin-card-title">Administrators</h2>
                <span className="super-admin-count-badge">{admins.length}</span>
              </div>
              
              <div className="admin-list">
                <AnimatePresence mode="popLayout">
                  {admins.length === 0 ? (
                    <motion.div 
                      className="empty-state"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="empty-state-icon">👥</div>
                      <div className="empty-state-text">No administrators yet</div>
                    </motion.div>
                  ) : (
                    admins.map((a, idx) => (
                      <motion.div
                        key={a._id}
                        className="admin-item"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.15, delay: idx * 0.02, ease: "easeOut" }}
                        layout="position"
                        onClick={(e) => {
                          // avoid triggering when clicking internal buttons
                          if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
                          setSelectedAdmin(a);
                          loadAdminActivity(a);
                        }}
                        style={{ cursor:'pointer' }}
                      >
                        <div className="admin-item-header">
                          <div className="admin-info">
                            <h3 className="admin-name">{a.firstName} {a.lastName}</h3>
                            <div className="admin-meta">
                              <span className="admin-email">{a.email}</span>
                              <span className={`admin-role-badge ${a.role === 'superadmin' ? 'superadmin' : ''}`}>
                                {a.role || (a.isAdmin ? 'admin' : 'user')}
                              </span>
                            </div>
                          </div>
                          <div className="admin-actions">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setResetOpen(o => ({ ...o, [a._id]: !o[a._id] }))}
                              aria-label={resetOpen[a._id] ? 'Cancel password reset' : 'Reset password'}
                            >
                              {resetOpen[a._id] ? 'Cancel' : 'Reset PW'}
                            </button>
                            {a.role !== 'superadmin' && (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => deleteAdmin(a._id)}
                                aria-label={`Delete ${a.firstName} ${a.lastName}`}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                        
                        <AnimatePresence>
                          {resetOpen[a._id] && (
                            <motion.div
                              className="password-reset-form"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                            >
                              <input
                                type="password"
                                className="form-input"
                                placeholder="New password (min 8 characters)"
                                value={resetValues[a._id] || ''}
                                onChange={e => setResetValues(v => ({ ...v, [a._id]: e.target.value }))}
                                aria-label="New password"
                              />
                              <button
                                className="btn btn-primary btn-sm"
                                disabled={resetBusy[a._id]}
                                onClick={() => resetPassword(a._id)}
                                aria-label="Save new password"
                              >
                                {resetBusy[a._id] ? 'Saving...' : 'Save'}
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Create Admin Card */}
            <motion.div 
              className="super-admin-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.08, ease: "easeOut" }}
            >
              <div className="super-admin-card-header">
                <h2 className="super-admin-card-title">Create Administrator</h2>
              </div>
              
              <form className="super-admin-form" onSubmit={e => { e.preventDefault(); createAdmin(); }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="First Name"
                  value={form.firstName}
                  onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                  aria-label="First Name"
                  required
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Last Name"
                  value={form.lastName}
                  onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                  aria-label="Last Name"
                  required
                />
                <input
                  type="email"
                  className="form-input"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  aria-label="Email Address"
                  required
                />
                <input
                  type="password"
                  className="form-input"
                  placeholder="Password (min 8 characters)"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  aria-label="Password"
                  minLength={8}
                  required
                />
                <button 
                  type="submit"
                  className="btn btn-primary btn-full"
                  aria-label="Create new administrator"
                >
                  Create Administrator
                </button>
              </form>
            </motion.div>

            {/* Activity Logs Card */}
            <motion.div 
              className="super-admin-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: 0.1, ease: "easeOut" }}
            >
              <div className="super-admin-card-header">
                <h2 className="super-admin-card-title">Activity Logs</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={exportActivityLogs}
                    style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                    title="Export logs as CSV"
                  >
                    📥 Export CSV
                  </button>
                  <span className="super-admin-count-badge">{logs.length}</span>
                </div>
              </div>
              
              <div className="activity-logs">
                <AnimatePresence mode="popLayout">
                  {logs.length === 0 ? (
                    <motion.div 
                      className="empty-state"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="empty-state-icon">📋</div>
                      <div className="empty-state-text">No activity logged yet</div>
                    </motion.div>
                  ) : (
                    logs.map((l, idx) => {
                      const ts = new Date(l.timestamp).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      });
                      // Derive friendly target/meta summary
                      let friendly = l.target;
                      if (l.category === 'admin' && l.meta?.email) friendly = l.meta.email;
                      if (l.category === 'product' && l.meta?.productName) friendly = l.meta.productName;
                      if (l.category === 'promotion' && l.meta?.code) friendly = l.meta.code;
                      if (l.category === 'reward' && l.meta?.rewardName) friendly = l.meta.rewardName;
                      if (l.category === 'customer' && l.meta?.customerEmail) friendly = l.meta.customerEmail;
                      if (l.category === 'order' && l.meta?.orderTotal) friendly = `${l.target.slice(-6)} ($${l.meta.orderTotal})`;
                      if (l.category === 'review' && l.action === 'add_review' && l.meta?.rating) friendly = `${l.meta.rating}★ on ${l.meta.productName||l.target}`;
                      if (l.category === 'delivery' && l.meta?.province && l.meta?.city) friendly = `${l.meta.province} • ${l.meta.city}`;
                      const metaSummary = (() => {
                        if (l.action === 'create-admin' && l.meta?.email) return `new admin ${l.meta.email}`;
                        if (l.action === 'update-admin' && l.meta?.update) return `fields: ${Object.keys(l.meta.update).join(', ')}`;
                        if (l.action === 'delete-admin') return 'admin removed';
                        if (l.action === 'create' && l.category === 'product' && l.meta?.price !== undefined) return `₱${l.meta.price} • stock ${l.meta.stock}`;
                        if (l.action === 'update' && l.category === 'product' && l.meta?.changes) return Object.keys(l.meta.changes).join(', ');
                        if (l.action === 'stock_add' && l.meta?.quantityAdded) return `+${l.meta.quantityAdded} (→ ${l.meta.newStock})`;
                        if (l.action === 'stock_remove' && l.meta?.quantityRemoved) return `-${l.meta.quantityRemoved} (→ ${l.meta.newStock})`;
                        if (l.category === 'promotion' && l.meta?.discountType) return `${l.meta.discountType} = ${l.meta.value}`;
                        if (l.action === 'redeem_reward' && l.meta?.voucherValue) return `redeemed ${l.meta.voucherValue}`;
                        if (l.action === 'login_failed') return 'failed login';
                        if (l.action === 'login') return 'login success';
                        if (l.action === 'email_verified') return 'email verified';
                        if (l.action === 'password_reset') return 'password reset';
                        if (l.action === 'add_address') return `address added (${l.meta?.city||''})`; 
                        if (l.action === 'delete_address') return 'address deleted';
                        if (l.action === 'restore' && l.category === 'customer') return 'account restored';
                        if (l.action === 'broadcast_message') return 'broadcast message';
                        if (l.action === 'send_message') return `msg to ${l.meta?.recipientEmail||'user'}`;
                        if (l.action === 'run_archive') return `archived (days=${l.meta?.retentionDays||''})`;
                        if (l.action === 'restore_archive') return 'archive restored';
                        if (l.action === 'delete_archive') return 'archive deleted';
                        if (l.action === 'rename_category') return `${l.meta?.oldName}→${l.meta?.newName}`;
                        if (l.action === 'reorder_categories' && l.meta?.categoryCount) return `${l.meta.categoryCount} categories reordered`;
                        return '';
                      })();
                      return (
                        <motion.div
                          key={idx}
                          className="log-item"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.1 }}
                          layout="position"
                          title={`Raw Target: ${l.target}\nActor ID: ${l.actorId}`}
                        >
                          <div className="log-timestamp">{ts}</div>
                          <div className="log-action">
                            <span className="log-category-badge" style={{
                              background: '#eef2ff', color: '#4338ca', padding: '2px 6px', borderRadius: 6, fontSize: 11, fontWeight: 600, marginRight: 6
                            }}>{l.category}</span>
                            <strong>{l.action}</strong>
                            {' '}• <span>{friendly}</span>
                            {metaSummary && <span style={{ color: '#64748b', marginLeft: 6 }}>({metaSummary})</span>}
                          </div>
                          <div className="log-actor">by {l.actorEmail} <span style={{ color:'#94a3b8', fontSize:11 }}>({l.actorRole})</span></div>
                          {l.meta && Object.keys(l.meta).length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <button
                                onClick={() => setExpandedLogs(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                style={{
                                  background: 'none',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 4,
                                  padding: '4px 8px',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                  color: '#64748b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                              >
                                {expandedLogs[idx] ? '▼' : '▶'} Meta Data
                              </button>
                              <AnimatePresence>
                                {expandedLogs[idx] && (
                                  <motion.pre
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.12, ease: "easeOut" }}
                                    style={{
                                      marginTop: 6,
                                      padding: 8,
                                      background: '#f8fafc',
                                      borderRadius: 6,
                                      fontSize: 11,
                                      overflow: 'auto',
                                      maxHeight: 200
                                    }}
                                  >
                                    {JSON.stringify(l.meta, null, 2)}
                                  </motion.pre>
                                )}
                              </AnimatePresence>
                            </div>
                          )}
                        </motion.div>
                      );
                    })
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </div>
      </div>
      {/* Admin Activity Modal */}
      <AnimatePresence>
        {selectedAdmin && (
          <motion.div
            className="admin-activity-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
          >
            <motion.div
              initial={{ scale:0.95, opacity:0 }}
              animate={{ scale:1, opacity:1 }}
              exit={{ scale:0.95, opacity:0 }}
              transition={{ duration:0.18, ease: "easeOut" }}
              style={{ width:'100%', maxWidth:'860px', maxHeight:'80vh', background:'#fff', borderRadius:'18px', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 10px 35px -5px rgba(0,0,0,0.3)' }}
            >
              <div style={{ padding:'1rem 1.25rem', background:'#175c36', color:'#fff', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <h3 style={{ margin:0, fontSize:'1.1rem' }}>Activity • {selectedAdmin.firstName} {selectedAdmin.lastName}</h3>
                  <div style={{ fontSize:'0.75rem', opacity:0.85 }}>{selectedAdmin.email} · {selectedAdmin.role}</div>
                </div>
                <button className="btn btn-danger btn-sm" onClick={() => { setSelectedAdmin(null); setAdminActivity([]); setAdminActivityMeta({ hasMore:false, offset:0, limit:40 }); }} aria-label="Close activity modal">Close</button>
              </div>
              <div style={{ padding:'0.75rem 1rem', flex:'1 1 auto', overflowY:'auto' }}>
                {adminActivityLoading ? (
                  <div style={{ padding:'1rem', textAlign:'center' }}>Loading logs...</div>
                ) : adminActivity.length === 0 ? (
                  <div style={{ padding:'1rem', textAlign:'center', color:'#555' }}>No activity recorded for this admin.</div>
                ) : (
                  <>
                    {adminActivity.map((l, i) => (
                      <div key={i} style={{ display:'flex', flexDirection:'column', padding:'0.6rem 0', borderBottom:'1px solid #eee' }}>
                        <div style={{ fontSize:'0.7rem', color:'#666' }}>{new Date(l.timestamp).toLocaleString()}</div>
                        <div style={{ fontSize:'0.85rem', fontWeight:600 }}>{l.category} • {l.action}</div>
                        <div style={{ fontSize:'0.75rem', color:'#333' }}>Target: {l.target}</div>
                        {l.meta && Object.keys(l.meta).length > 0 && (
                          <details style={{ marginTop:'0.3rem' }}>
                            <summary style={{ cursor:'pointer', fontSize:'0.7rem', color:'#175c36' }}>Meta</summary>
                            <pre style={{ margin:0, fontSize:'0.65rem', whiteSpace:'pre-wrap' }}>{JSON.stringify(l.meta, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    ))}
                    {adminActivityMeta.hasMore && (
                      <button
                        disabled={adminActivityLoading}
                        onClick={() => {
                          if (adminActivityLoading) return;
                          const nextOffset = adminActivityMeta.offset + adminActivityMeta.limit;
                          setAdminActivityLoading(true);
                          fetch(`${process.env.REACT_APP_API}/api/admin/users/activity/${selectedAdmin._id}?limit=${adminActivityMeta.limit}&offset=${nextOffset}`, { headers: { Authorization: `Bearer ${token}` } })
                            .then(r => r.json())
                            .then(data => {
                              setAdminActivity(prev => [...prev, ...data.entries]);
                              setAdminActivityMeta({
                                hasMore: data.hasMore,
                                offset: data.offset,
                                limit: data.limit
                              });
                            })
                            .catch(err => console.error('Failed to load more admin activity', err))
                            .finally(() => setAdminActivityLoading(false));
                        }}
                        style={{ marginTop:'0.75rem', padding:'0.5rem 0.75rem', background:'#175c36', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.75rem' }}
                      >{adminActivityLoading ? 'Loading...' : 'Load More'}</button>
                    )}
                  </>
                )}
              </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}

export default SuperAdminPage;
